import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PassCard from './PassCard';
import { PassData } from './types';
import { WalletClientEvent } from '../../lib/telemetry';

// Multi-pass (group link) list — docs/MULTIPASS_SEND_PLAN.md. One SMS link
// resolves to N vouchers; each renders as a full PassCard with its own
// platform-appropriate add buttons. Group members arrive in the single-pass
// preview shape plus that member's security_key (needed for the per-pass
// /apple/{keyword}?k=... download).

export interface GroupPassData extends PassData {
  security_key?: string;
}

const Spinner: React.FC = () => (
  <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const MultiPassList: React.FC<{
  passes: GroupPassData[];
  isIOS: boolean;
  isAndroid: boolean;
  buildApiUrl: (suffix: string) => string;
  onTrack: (keyword: string, event: WalletClientEvent) => void;
  onFollowup: (keyword: string, securityKey?: string) => void;
  onAnyAddClicked: () => void;
  // "Add all" (Phase B): the group link's own keyword + security key drive the
  // /apple/{group} .pkpasses bundle; googleAllJwt is the combined save JWT.
  groupKeyword?: string;
  groupSecurityKey?: string;
  googleAllJwt?: string;
}> = ({ passes, isIOS, isAndroid, buildApiUrl, onTrack, onFollowup, onAnyAddClicked, groupKeyword, groupSecurityKey, googleAllJwt }) => {
  const [downloadingKeyword, setDownloadingKeyword] = useState<string | null>(null);
  const [flippedKeyword, setFlippedKeyword] = useState<string | null>(null);

  // Same platform logic as WalletCtaPanel: Apple on iOS, Google everywhere
  // else (including desktop/unknown).
  const showApple = isIOS;
  const showGoogle = !isIOS;

  const handleAppleAll = async () => {
    if (!groupKeyword || downloadingKeyword) return;
    setDownloadingKeyword('__all__');
    onAnyAddClicked();
    onTrack(groupKeyword, 'WALLET_APPLE_BUTTON_CLICKED');
    passes.forEach((p) => p.keyword && onFollowup(p.keyword, p.security_key));

    const securityKeyParam = groupSecurityKey ? `?k=${encodeURIComponent(groupSecurityKey)}` : '';
    const apiUrl = buildApiUrl(`/apple/${groupKeyword}${securityKeyParam}`);
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: { 'Accept': 'application/json', 'X-Request-Download-Url': 'true' },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.downloadUrl) {
          window.location.href = data.downloadUrl;
        } else {
          console.error('No download URL in bundle response:', data);
        }
      } else {
        window.location.href = apiUrl;
      }
    } catch (err) {
      console.error('Error downloading pass bundle:', err);
      window.location.href = apiUrl;
    }
    setTimeout(() => setDownloadingKeyword(null), 8000);
  };

  const handleGoogleAll = () => {
    if (!googleAllJwt) return;
    onAnyAddClicked();
    if (groupKeyword) onTrack(groupKeyword, 'WALLET_GOOGLE_BUTTON_CLICKED');
    passes.forEach((p) => p.keyword && onFollowup(p.keyword, p.security_key));
    window.open(`https://pay.google.com/gp/v/save/${googleAllJwt}`, '_blank', 'noopener');
  };

  const showAddAll = passes.length > 1 && ((showApple && !!groupKeyword) || (showGoogle && !!googleAllJwt));

  const handleApple = async (pass: GroupPassData) => {
    const kw = pass.keyword;
    if (!kw || downloadingKeyword) return;
    setDownloadingKeyword(kw);
    onAnyAddClicked();
    onTrack(kw, 'WALLET_APPLE_BUTTON_CLICKED');
    onFollowup(kw, pass.security_key);

    const securityKeyParam = pass.security_key ? `?k=${encodeURIComponent(pass.security_key)}` : '';
    const apiUrl = buildApiUrl(`/apple/${kw}${securityKeyParam}`);
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Request-Download-Url': 'true',
        },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.downloadUrl) {
          window.location.href = data.downloadUrl;
        } else {
          console.error('No download URL in response:', data);
        }
      } else {
        window.location.href = apiUrl;
      }
    } catch (err) {
      console.error('Error downloading Apple pass:', err);
      window.location.href = apiUrl;
    }
    setTimeout(() => setDownloadingKeyword(null), 8000);
  };

  const handleGoogle = (pass: GroupPassData) => {
    if (!pass.keyword || !pass.googleJwt) return;
    onAnyAddClicked();
    onTrack(pass.keyword, 'WALLET_GOOGLE_BUTTON_CLICKED');
    onFollowup(pass.keyword, pass.security_key);
    window.open(`https://pay.google.com/gp/v/save/${pass.googleJwt}`, '_blank', 'noopener');
  };

  return (
    <div className="space-y-8">
      {showAddAll && (
        <div className="max-w-md mx-auto w-full">
          <div className="rounded-2xl bg-white border border-[#E4EAE2] p-4">
            <p className="text-sm font-semibold text-gray-800 mb-3 text-center">
              Add all {passes.length} passes in one go
            </p>
            <div className="space-y-2">
              {showApple && groupKeyword && (
                <button
                  type="button"
                  onClick={handleAppleAll}
                  disabled={downloadingKeyword === '__all__'}
                  className="w-full h-[52px] rounded-xl bg-black flex items-center justify-center hover:opacity-85 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {downloadingKeyword === '__all__'
                    ? <Spinner />
                    : <img src="/images/appleWalletLogo.svg" alt="Add all to Apple Wallet" className="h-11 w-auto max-w-full object-contain" />}
                </button>
              )}
              {showGoogle && googleAllJwt && (
                <button
                  type="button"
                  onClick={handleGoogleAll}
                  className="w-full h-[52px] rounded-xl bg-black flex items-center justify-center hover:opacity-85 transition"
                >
                  <img src="/images/googleWalletLogo.svg" alt="Add all to Google Wallet" className="h-11 w-auto max-w-full object-contain" />
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-3 text-center">
              Or add passes individually below.
            </p>
          </div>
        </div>
      )}
      {passes.map((pass, i) => {
        const kw = pass.keyword || String(i);
        const title = pass.position_header?.value || pass.organisation_name;
        return (
          <motion.div
            key={kw}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: i * 0.06 }}
            className="max-w-md mx-auto w-full"
          >
            <div className="flex items-baseline justify-between mb-2 px-1">
              <span className="text-sm font-semibold text-gray-700">
                Pass {i + 1} of {passes.length}
              </span>
              {title && (
                <span className="text-sm text-gray-500 truncate max-w-[60%] text-right">{title}</span>
              )}
            </div>

            <PassCard
              passData={pass}
              isFlipped={flippedKeyword === kw}
              onFlip={() => setFlippedKeyword((v) => (v === kw ? null : kw))}
            />

            <div className="mt-3 space-y-2">
              {showApple && (
                <button
                  type="button"
                  onClick={() => handleApple(pass)}
                  disabled={downloadingKeyword === kw}
                  className="w-full h-[52px] rounded-xl bg-black flex items-center justify-center hover:opacity-85 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {downloadingKeyword === kw
                    ? <Spinner />
                    : <img src="/images/appleWalletLogo.svg" alt="Add to Apple Wallet" className="h-11 w-auto max-w-full object-contain" />}
                </button>
              )}
              {showGoogle && (
                <button
                  type="button"
                  onClick={() => handleGoogle(pass)}
                  disabled={!pass.googleJwt}
                  className="w-full h-[52px] rounded-xl bg-black flex items-center justify-center hover:opacity-85 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <img src="/images/googleWalletLogo.svg" alt="Add to Google Wallet" className="h-11 w-auto max-w-full object-contain" />
                </button>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default MultiPassList;
